/**
 * ProductCard — custom element that handles:
 *  - Image slider (scroll-based, no dependency on SliderComponent)
 *  - Color swatch clicks → slide sync + variant resolution
 *  - Size pill clicks → variant resolution
 *  - Direct add-to-cart form variant update
 */
class ProductCard extends HTMLElement {
  isDesktopView() {
    return window.matchMedia('(min-width: 750px)').matches;
  }

  connectedCallback() {
    this.cardId = this.dataset.pcCard;

    // ── Variant data (injected by Liquid into <script type="application/json">) ──
    const dataEl = this.querySelector('[data-pc-variant-data]');
    if (dataEl) {
      try {
        const parsed = JSON.parse(dataEl.textContent);
        this.variants     = parsed.variants || [];
        this.colorOptIdx  = parsed.colorOptIdx;
        this.sizeOptIdx   = parsed.sizeOptIdx;
      } catch (e) {
        this.variants = [];
        this.colorOptIdx = -1;
        this.sizeOptIdx  = -1;
      }
    } else {
      this.variants = [];
      this.colorOptIdx = -1;
      this.sizeOptIdx  = -1;
    }

    this.selectedColor = null;
    this.selectedSize  = null;

    this.initSlider();
    this.initSwatches();
    this.initSizePills();
    this.seedSelections();
    this.updateAtcForm();
  }

  // ── Slider ────────────────────────────────────────────────────────────────

  initSlider() {
    this.track        = this.querySelector('[data-card-slider-track]');
    this.slider       = this.querySelector('.card__media-slider');
    this.prevBtn      = this.querySelector('[data-slider-prev]');
    this.nextBtn      = this.querySelector('[data-slider-next]');
    this.activeSlide  = 0;
    this.hasNavigated = false;
    this.loopResetting = false;
    this.cardMode = this.slider?.dataset.cardMode || 'single';
    this.hoverMode = this.cardMode === 'hover';
    this.mobileTwoSlider = this.slider?.classList.contains('card__media-slider--mobile-2');

    if (this.track && (this.cardMode === 'slider' || this.mobileTwoSlider)) {
      this.setupLoopClone();
      this.bindSwipeGuard();
    }

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.goPrev();
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.goNext();
      });
    }

    if (this.track) {
      this.track.addEventListener('scroll', () => {
        if (this.loopResetting) return;
        clearTimeout(this._scrollSyncTimer);
        this._scrollSyncTimer = setTimeout(() => {
          this.syncSlideFromScroll();
        }, 80);
      }, { passive: true });
    }

    this.addEventListener('mouseleave', () => {
      this.slider?.classList.remove('suppress-hover-secondary');
    });

    this.updateSliderState(0, true);
  }

  bindSwipeGuard() {
    if (!this.track) return;

    let startX = 0;
    let swiping = false;

    this.track.addEventListener('touchstart', (event) => {
      startX = event.touches[0].clientX;
      swiping = false;
    }, { passive: true });

    this.track.addEventListener('touchmove', (event) => {
      if (Math.abs(event.touches[0].clientX - startX) > 8) swiping = true;
    }, { passive: true });

    this.track.addEventListener('click', (event) => {
      if (!swiping) return;
      event.preventDefault();
      event.stopPropagation();
      swiping = false;
    }, true);
  }

  setupLoopClone() {
    const logical = this.getLogicalSlides();
    if (logical.length <= 1) return;

    const clone = logical[0].cloneNode(true);
    clone.classList.add('card__media-slide--loop-clone');
    clone.removeAttribute('href');
    this.track.appendChild(clone);
  }

  getLogicalSlides() {
    if (!this.track) return [];
    return Array.from(
      this.track.querySelectorAll('.card__media-slide:not(.card__media-slide--loop-clone)')
    );
  }

  getSlideWidth() {
    return this.track ? this.track.clientWidth : 0;
  }

  currentSlideIndex() {
    if (!this.track) return 0;
    const width = this.getSlideWidth();
    if (!width) return 0;
    return Math.round(this.track.scrollLeft / width);
  }

  syncSlideFromScroll() {
    const logical = this.getLogicalSlides();
    const idx = this.currentSlideIndex();

    if (logical.length > 1 && idx >= logical.length) {
      this.snapToSlide(0, false, true);
      return;
    }

    this.updateSliderState(idx);
  }

  snapToSlide(idx, smooth = true, wrappedForward = false) {
    if (!this.track) return;
    const width = this.getSlideWidth();
    if (!width) return;

    this.loopResetting = !smooth;
    if (!smooth) {
      this.track.style.scrollSnapType = 'none';
    }

    this.track.scrollTo({
      left: idx * width,
      behavior: smooth ? 'smooth' : 'auto',
    });

    if (!smooth) {
      requestAnimationFrame(() => {
        this.track.style.scrollSnapType = '';
        this.loopResetting = false;
        this.updateSliderState(idx, false, wrappedForward);
      });
    } else {
      this.updateSliderState(idx);
    }
  }

  updateSliderState(idx, isInitial = false, wrappedForward = false) {
    if (!this.slider) return;

    const returningToStart =
      idx === 0 && (wrappedForward || (this.hasNavigated && this.activeSlide !== 0));
    this.activeSlide = idx;

    this.slider.classList.toggle('is-at-start', idx === 0);

    if (idx !== 0) {
      this.hasNavigated = true;
      this.slider.classList.remove('suppress-hover-secondary');
    } else if (returningToStart && !isInitial) {
      this.slider.classList.add('suppress-hover-secondary');
    }
  }

  goToSlide(idx, smooth = true) {
    if (!this.track) return;
    const logical = this.getLogicalSlides();
    if (idx < 0 || idx >= logical.length) return;
    this.snapToSlide(idx, smooth);
  }

  goNext() {
    if (this.mobileTwoSlider && this.isDesktopView()) return;
    const logical = this.getLogicalSlides();
    if (logical.length <= 1 || !this.track) return;

    const cur = this.currentSlideIndex();
    const lastIdx = logical.length - 1;
    const width = this.getSlideWidth();
    if (!width) return;

    if (cur >= lastIdx) {
      this.track.scrollTo({ left: logical.length * width, behavior: 'smooth' });
    } else {
      this.goToSlide(cur + 1);
    }
  }

  goPrev() {
    if (this.mobileTwoSlider && this.isDesktopView()) return;
    const logical = this.getLogicalSlides();
    if (logical.length <= 1 || !this.track) return;

    const cur = this.currentSlideIndex();
    const lastIdx = logical.length - 1;
    const width = this.getSlideWidth();
    if (!width) return;

    if (cur <= 0) {
      this.loopResetting = true;
      this.track.style.scrollSnapType = 'none';
      this.track.scrollLeft = logical.length * width;
      requestAnimationFrame(() => {
        this.track.style.scrollSnapType = '';
        this.loopResetting = false;
        this.goToSlide(lastIdx);
      });
    } else {
      this.goToSlide(cur - 1);
    }
  }

  updateHoverStack(swatch) {
    if (!swatch) return;
    const primaryUrl = swatch.getAttribute('data-image-url');
    const hoverUrl = swatch.getAttribute('data-hover-image-url');
    const logical = this.getLogicalSlides();
    const firstSlide = logical[0];
    if (!firstSlide || !primaryUrl) return;

    const container = firstSlide.querySelector('.media--hover-effect');
    const imgs = container ? container.querySelectorAll('img') : [];

    if (imgs.length >= 2) {
      imgs[0].src = primaryUrl;
      imgs[0].srcset = primaryUrl;
      if (hoverUrl) {
        imgs[1].src = hoverUrl;
        imgs[1].srcset = hoverUrl;
      }
    }
  }

  updateHoverImages(swatch) {
    if (!swatch) return;
    this.updateHoverStack(swatch);

    const logical = this.getLogicalSlides();
    const firstSlide = logical[0];
    if (firstSlide) {
      const container = firstSlide.querySelector('.media--hover-effect');
      if (!container) {
        const primaryImg = firstSlide.querySelector('img.card__slide-img') || firstSlide.querySelector('img');
        const primaryUrl = swatch.getAttribute('data-image-url');
        if (primaryImg && primaryUrl) {
          primaryImg.src = primaryUrl;
          primaryImg.srcset = primaryUrl;
        }
      }
    }

    if (this.slider) {
      this.slider.classList.add('is-at-start');
      this.slider.classList.remove('suppress-hover-secondary');
    }
    this.activeSlide = 0;
  }

  updateMobilePrimaryImage(swatch) {
    if (!swatch) return;
    const slideIdx = parseInt(swatch.getAttribute('data-slide-idx') || '0', 10);
    const logical = this.getLogicalSlides();
    const firstSlide = logical[0];
    if (!firstSlide) return;

    const targetSlide = logical[slideIdx] || firstSlide;
    const targetImg = targetSlide.querySelector('img.card__slide-img') || targetSlide.querySelector('img');
    const primaryImg = firstSlide.querySelector('img.card__slide-img') || firstSlide.querySelector('img');
    if (!primaryImg) return;

    if (targetImg) {
      primaryImg.src = targetImg.currentSrc || targetImg.src;
      if (targetImg.srcset) {
        primaryImg.srcset = targetImg.srcset;
      } else {
        primaryImg.srcset = primaryImg.src;
      }
      primaryImg.alt = targetImg.alt;
      return;
    }

    const imageUrl = swatch.getAttribute('data-image-url');
    if (!imageUrl) return;
    primaryImg.src = imageUrl;
    primaryImg.srcset = imageUrl;
  }

  // ── Swatches ──────────────────────────────────────────────────────────────

  initSwatches() {
    this.swatches = this.querySelectorAll(`[data-card-id="${this.cardId}"]`);
    this.swatches.forEach((swatch) => {
      swatch.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.swatches.forEach((s) => s.classList.remove('is-active'));
        swatch.classList.add('is-active');
        this.selectedColor = swatch.getAttribute('title');
        this.updateAtcForm();
        const slideIdx = parseInt(swatch.getAttribute('data-slide-idx') || '0', 10);
        if (this.hoverMode) {
          this.updateHoverImages(swatch);
        } else if (this.cardMode === 'slider') {
          if (this.isDesktopView()) {
            this.goToSlide(slideIdx);
          } else {
            this.updateMobilePrimaryImage(swatch);
          }
          if (this.slider?.classList.contains('card__media-slider--hover-secondary')) {
            this.updateHoverStack(swatch);
          }
        } else if (swatch.getAttribute('data-image-url')) {
          this.updateHoverImages(swatch);
        }
        const bundleCard = swatch.closest('[data-bundle-card]');
        if (bundleCard) {
          const vid    = swatch.getAttribute('data-variant-id');
          const imgUrl = swatch.getAttribute('data-image-url');
          if (vid)    bundleCard.setAttribute('data-variant-id',    vid);
          if (imgUrl) bundleCard.setAttribute('data-product-image', imgUrl);
          bundleCard.dispatchEvent(new CustomEvent('bundle:swatch-changed', {
            bubbles: true,
            detail: {
              productId: bundleCard.dataset.productId,
              variantId: vid,
              image:     imgUrl,
            },
          }));
        }
      });
    });
  }

  // ── Size pills ────────────────────────────────────────────────────────────

  initSizePills() {
    this.sizePills = this.querySelectorAll(`[data-size-card-id="${this.cardId}"]`);
    this.sizePills.forEach((pill) => {
      pill.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.sizePills.forEach((p) => p.classList.remove('is-active'));
        pill.classList.add('is-active');
        this.selectedSize = pill.getAttribute('data-size-value');
        this.updateAtcForm();
      });
    });
  }

  // ── Seed initial state from pre-active elements ───────────────────────────

  seedSelections() {
    const activeSwatch = this.querySelector(`[data-card-id="${this.cardId}"].is-active`);
    if (activeSwatch) {
      this.selectedColor = activeSwatch.getAttribute('title');
    } else {
      const firstSwatch = this.querySelector(`[data-card-id="${this.cardId}"]`);
      if (firstSwatch) this.selectedColor = firstSwatch.getAttribute('title');
    }

    const activePill = this.querySelector(`[data-size-card-id="${this.cardId}"].is-active`);
    if (activePill) {
      this.selectedSize = activePill.getAttribute('data-size-value');
    } else {
      const firstPill = this.querySelector(`[data-size-card-id="${this.cardId}"]`);
      if (firstPill) this.selectedSize = firstPill.getAttribute('data-size-value');
    }
  }

  // ── Variant resolution ────────────────────────────────────────────────────

  resolveVariant() {
    let best = null;
    for (const v of this.variants) {
      // null selectedColor/selectedSize means no selection yet — treat as wildcard
      const colorMatch = this.colorOptIdx < 0 || this.selectedColor == null || v.o[this.colorOptIdx] === this.selectedColor;
      const sizeMatch  = this.sizeOptIdx  < 0 || this.selectedSize  == null || v.o[this.sizeOptIdx]  === this.selectedSize;
      if (colorMatch && sizeMatch) {
        if (!best || v.avail) best = v;
        if (v.avail) break;
      }
    }
    // Fallback: match color only (when a specific color is selected but size has no match)
    if (!best && this.colorOptIdx >= 0 && this.selectedColor != null) {
      for (const v of this.variants) {
        if (v.o[this.colorOptIdx] === this.selectedColor) {
          if (!best || v.avail) best = v;
          if (v.avail) break;
        }
      }
    }
    return best || this.variants[0] || null;
  }

  // ── Update all ATC forms in this card ────────────────────────────────────

  updateAtcForm() {
    const v = this.resolveVariant();
    this.querySelectorAll('.product-variant-id').forEach((input) => {
      if (v) input.value = v.id;
    });
    this.querySelectorAll('.quick-add__submit').forEach((btn) => {
      btn.disabled = !v || !v.avail;
    });
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}
